import { AxiosHeaders } from 'axios';

import { HttpHeader } from '../../../common/enums';
import { fetchAuthToken } from '../api';
import { StorageKey } from '../enums';
import { WebStoreApiAuthTokensRequestBody } from '../models';
import { getAuthBearerValue } from './get-auth-bearer-value';
import { isTokenExpired } from './is-token-expired';
import { refreshToken } from './refresh-token';
import { validateJWTToken } from './validate-jwt-token';

import { getWebStoreApiConfig } from '../config';

/**
 * Gets authorized JWT from either storage or Web Store API.
 * If JWT in storage is about to be expired it will be refreshed.
 * @param accessToken Access JWT to authorize (UIP)
 * @param guestToken Guest JWT to associate with authorized one
 * @returns Promise with authorized JWT
 */
export const getAuthToken = async (
  accessToken: string,
  guestToken: string | null
): Promise<string | null> => {
  const { storage } = getWebStoreApiConfig();
  const storedToken: string | null = storage.getItem(StorageKey.WebStoreAuthToken);
  if (storedToken && validateJWTToken(storedToken)) {
    return storedToken;
  }

  let token: string | null;
  let customerId: string | null = null;

  if (storedToken && !isTokenExpired(storedToken)) {
    // If token is not expired then it will be refreshed.
    token = await refreshToken(storedToken, accessToken);
  } else {
    // If token either does not exist or is expired it will be replaced with new one.
    const { marketLocale, siteContext } = getWebStoreApiConfig();
    if (!siteContext || !guestToken) {
      console.error('getAuthToken called before siteContext was configured');
      return null;
    }
    const body: WebStoreApiAuthTokensRequestBody = { brand: siteContext, guestToken, marketLocale };
    const headers: AxiosHeaders = new AxiosHeaders();
    headers.set(HttpHeader.Authorization, getAuthBearerValue(accessToken));
    const response = await fetchAuthToken(body, headers);

    token = response.token;
    customerId = response.customerId;
  }

  if (!token) {
    return Promise.reject('Authorization header was not found in auth token response');
  }

  storage.setItem(StorageKey.WebStoreAuthToken, token);
  if (customerId) {
    storage.setItem(StorageKey.CustomerAuthId, customerId);
  }

  return token;
};
